import type { AtomicBreakdownEntry } from '@/types/api'
import { statusBadgeConfig, type StatusKey } from './statusConfig'

interface TriageCardProps {
  entry: AtomicBreakdownEntry
  claimIndex: number
  isSelected: boolean
  /** Effective post-review status used for the badge. Callers should pass
   *  the in-app snapshot's ``effectiveStatus`` (which may differ from
   *  ``entry.status`` once a writer hides sources). The share view passes
   *  ``entry.status`` since there's no live review state. */
  effectiveStatus: StatusKey
  /** D12: a writer removed this claim's citation from the doc this session.
   *  Renders the "Removed from text" pill and the Restore button. The share
   *  view always passes ``false``. */
  isRemoved: boolean
  onSelect: () => void
  /** Only invoked when ``isRemoved`` is true. */
  onRestore?: () => void
}

export function TriageCard({
  entry,
  claimIndex,
  isSelected,
  effectiveStatus,
  isRemoved,
  onSelect,
  onRestore,
}: TriageCardProps) {
  const badge = statusBadgeConfig[effectiveStatus] ?? statusBadgeConfig['unverified']

  // Collect unique source UUIDs for count
  const seen = new Set<string>()
  const evidences = entry.evidences ?? []
  for (const ev of evidences) {
    if (ev?.source_doc) seen.add(ev.source_doc)
  }

  const sourceCount = seen.size
  const conflictCount = evidences.filter(ev => ev?.support_level === 'contradicts').length

  const sourceSummary = sourceCount === 0
    ? null
    : conflictCount > 0
      ? `${sourceCount} source${sourceCount !== 1 ? 's' : ''} · ${conflictCount} conflict${conflictCount !== 1 ? 's' : ''}`
      : `${sourceCount} source${sourceCount !== 1 ? 's' : ''}`

  function handleRestore(e: React.MouseEvent) {
    e.stopPropagation()
    onRestore?.()
  }

  return (
    <div
      id={`triage-card-${claimIndex}`}
      data-claim-index={claimIndex}
      role="button"
      tabIndex={0}
      className={`bg-card border rounded-lg px-3 py-2.5 cursor-pointer transition-all duration-150 ${
        isSelected ? 'border-primary/50' : 'border-border/40 hover:border-border'
      } ${isRemoved ? 'opacity-55' : ''}`}
      onClick={onSelect}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect() } }}
    >
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1.5">
          <span className={`text-[10px] font-medium px-2 py-0.5 rounded-[10px] ${badge.className}`}>
            {badge.label}
          </span>
          {isRemoved && (
            <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-[8px] bg-muted text-muted-foreground uppercase tracking-wide">
              Removed from text
            </span>
          )}
        </div>
        {isRemoved && onRestore ? (
          <button
            type="button"
            className="text-[10px] px-2 py-0.5 rounded-md text-primary hover:bg-primary/10 transition-colors"
            onClick={handleRestore}
          >
            Restore
          </button>
        ) : (
          <span className="text-[10px] text-muted-foreground">→</span>
        )}
      </div>
      <p className="text-[11px] font-medium text-foreground leading-[1.4] line-clamp-2">
        {entry.claim}
      </p>
      {sourceSummary && (
        <p className="text-[10px] text-muted-foreground mt-1">{sourceSummary}</p>
      )}
    </div>
  )
}
